import { HttpFactoryService, HttpService } from '../../common/services';

export interface ICompany {
  id: number;
  createdAt: string;
  updatedAt: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  ownerId: number;
  featureToggles: Record<string, boolean>;
  maxSeats: number;
  isActive: boolean;
}

export interface ICompanyMember {
  id: number;
  createdAt: string;
  companyId: number;
  userId: number;
  role: string;
  qrToken: string;
  user: {
    id: number;
    email: string;
    displayName: string | null;
    avatar: string | null;
  };
}

export interface ICompanyFull extends ICompany {
  members: ICompanyMember[];
  _count: { members: number; queueTemplates: number; reviews: number };
}

export interface IQueueTemplate {
  id: number;
  createdAt: string;
  updatedAt: string;
  companyId: number;
  name: string;
  title: string;
  meetingUrl: string | null;
  participantLimit: number | null;
  password: string | null;
  settings: Record<string, any>;
  createdBy: { id: number; email: string; displayName: string | null };
}

class CompanyService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  async getCompanyPublic(slug: string) {
    return this.httpService.get<Partial<ICompany>>(`company/${slug}`);
  }

  async getCompanyFull(slug: string) {
    return this.httpService.get<ICompanyFull>(`company/${slug}/full`);
  }

  async updateCompany(slug: string, data: { name?: string; slug?: string }) {
    return this.httpService.put<ICompany>(`company/${slug}`, data);
  }

  async updateBranding(slug: string, data: { logoUrl?: string; primaryColor?: string; secondaryColor?: string; accentColor?: string }) {
    return this.httpService.put<ICompany>(`company/${slug}/branding`, data);
  }

  async updateFeatureToggles(slug: string, toggles: Record<string, boolean>) {
    return this.httpService.put<ICompany>(`company/${slug}/feature-toggles`, { toggles });
  }

  async getMembers(slug: string) {
    return this.httpService.get<ICompanyMember[]>(`company/${slug}/members`);
  }

  async addMember(slug: string, email: string, role?: string) {
    return this.httpService.post<ICompanyMember>(`company/${slug}/members`, { email, role });
  }

  async removeMember(slug: string, userId: number) {
    return this.httpService.delete(`company/${slug}/members/${userId}`);
  }

  async updateMemberRole(slug: string, userId: number, role: string) {
    return this.httpService.put(`company/${slug}/members/${userId}/role`, { role });
  }

  async getTemplates(slug: string) {
    return this.httpService.get<IQueueTemplate[]>(`company/${slug}/templates`);
  }

  async createTemplate(slug: string, data: { name: string; title: string; meetingUrl?: string; participantLimit?: number; password?: string; settings?: Record<string, any> }) {
    return this.httpService.post<IQueueTemplate>(`company/${slug}/templates`, data);
  }

  async updateTemplate(slug: string, id: number, data: Partial<{ name: string; title: string; meetingUrl: string; participantLimit: number; password: string; settings: Record<string, any> }>) {
    return this.httpService.put<IQueueTemplate>(`company/${slug}/templates/${id}`, data);
  }

  async deleteTemplate(slug: string, id: number) {
    return this.httpService.delete(`company/${slug}/templates/${id}`);
  }
}

const companyService = new CompanyService(new HttpFactoryService());
export { companyService };
